import { useEffect, useState, useCallback } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import httpClient from "api/httpClient";
import ConfirmationDialog from "components/Common/ConfirmationDialog";
import SchemaDialogForm from "components/Form/SchemaDialogForm";
import { customerFormSchema, customerRecord } from "./data/CustomerSchema";
import CustomersTableData from "./data/CustomersTableData";
import { useTranslation } from "react-i18next";
import { CustomTablePagination } from "components/Common/CustomTablePagination";
import { getRoleFromStorage } from "utils/auth";
import AgentTopup from "../agent-topup";
import AgentRedeem from "../agent-redeem";
import { useNavigate } from "react-router-dom";

export default function LoyaltyCustomers() {
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  const [searchInput, setSearchInput] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(customerRecord);
  const [dialogTitle, setDialogTitle] = useState("Create Customer");

  const [itemToDelete, setItemToDelete] = useState(null);
  const [confirmationDialogMessage, setConfirmationDialogMessage] = useState("");
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  const [snack, setSnack] = useState({ open: false, color: "success", content: "" });
  const openSnack = (color, content) => setSnack({ open: true, color, content });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const navigate = useNavigate();

  const loadCustomers = useCallback(
    async (q = "") => {
      try {
        setLoading(true);

        // Preferred (agents-style pagination/search):
        const url = q
          ? `/customers/search-list?page=${page}&size=${rowsPerPage}&q=${encodeURIComponent(q)}`
          : `/customers?page=${page}&size=${rowsPerPage}`;

        const res = await httpClient.get(url);

        // Safe fallback in case backend still returns {items: []}
        const content = res.data?.content || res.data?.items || [];
        setItems(content);
        setTotalElements(res.data?.totalElements ?? content.length);
      } catch (err) {
        openSnack(
          "error",
          err?.response?.data?.message || err.message || "Failed to load customers"
        );
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage]
  );

  const loadAgents = useCallback(async (search = "") => {
    if (getRoleFromStorage() === "AGENT") {
      return;
    }
    try {
      setAgentsLoading(true);

      // You already have /agents/search-list (admin only currently).
      // This should be used by ADMIN interface assigning customers.
      const url = search
        ? `/agents/search-list?page=0&size=20&q=${encodeURIComponent(search)}`
        : `/agents?page=0&size=20`;

      const res = await httpClient.get(url);
      const rows = res.data?.content || res.data?.items || [];

      // Map into label/value objects used by autocomplete/select
      setAgents(
        rows.map((a) => ({
          ...a,
          value: a.id,
          label: `${a.sc_code} - ${a.name}${a.phone ? ` (${a.phone})` : ""}`,
        }))
      );
    } catch (err) {
      // don’t hard-fail the whole customers page; just show a snack
      openSnack("error", err?.response?.data?.message || err.message || "Failed to load agents");
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers(searchInput);
    loadAgents("");
  }, [loadCustomers, searchInput, loadAgents]);

  const openCreate = () => {
    setEditingRecord({ ...customerRecord });
    setDialogTitle("Create Customer");
    setDialogOpen(true);
  };

  const openEditDialog = (rec) => {
    setEditingRecord({ ...rec });
    setDialogTitle("Edit Customer");
    setDialogOpen(true);
  };

  const payWithLoyaltyPoints = () => {
    navigate("/loyalty/redeem", { replace: true });
  };

  const topUp = () => {
    navigate("/loyalty/topup", { replace: true });
  };

  const submit = async (values) => {
    try {
      const payload = { ...values };

      if (values.id) {
        await httpClient.put(`/customers/${values.id}`, payload);
        openSnack("success", "Customer updated");
      } else {
        await httpClient.post(`/customers`, payload);
        openSnack("success", "Customer created");
      }

      setDialogOpen(false);
      loadCustomers(searchInput);
    } catch (e) {
      openSnack("error", e?.response?.data?.message || e.message || "Save failed");
    }
  };

  const onDeleteConfirmed = async () => {
    if (!itemToDelete) return;
    try {
      await httpClient.delete(`/customers/${itemToDelete.id}`);
      openSnack("success", "Customer deleted");
      setConfirmationDialogOpen(false);
      setItemToDelete(null);
      loadCustomers(searchInput);
    } catch (e) {
      openSnack("error", e?.response?.data?.message || e.message || "Delete failed");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDBox alignItems="left" display="flex" gap={1}>
            <MDButton color="info" onClick={openCreate}>
              <Icon>add</Icon>&nbsp;Create
            </MDButton>
            <MDButton color="success" onClick={topUp}>
              <Icon>arrow_upward</Icon>&nbsp;Top Up
            </MDButton>
            <MDButton color="warning" onClick={payWithLoyaltyPoints}>
              <Icon>payment</Icon>&nbsp;Pay
            </MDButton>
          </MDBox>

          <MDBox display="flex" gap={2} alignItems="center">
            <MDBox display="flex" gap={1}>
              <MDInput
                size="small"
                label="Search (card / phone / patient / name / email)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyUp={(e) => e.key === "Enter" && loadCustomers(searchInput)}
              />
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => loadCustomers(searchInput)}
              >
                {t("search.go")}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>

        <Card>
          <MDBox p={2}>
            {loading ? (
              <MDTypography variant="button">Loading...</MDTypography>
            ) : items.length === 0 ? (
              <MDTypography variant="button">No customers found.</MDTypography>
            ) : (
              <>
                <CustomersTableData
                  data={items}
                  onEdit={openEditDialog}
                  onDelete={(rec) => {
                    setItemToDelete(rec);
                    setConfirmationDialogMessage(`Delete ${rec.owner_name || rec.card_number}?`);
                    setConfirmationDialogOpen(true);
                  }}
                />

                <CustomTablePagination
                  page={page}
                  rowsPerPage={rowsPerPage}
                  totalItems={totalElements}
                  onPageChange={setPage}
                  onRowsPerPageChange={(n) => {
                    setRowsPerPage(n);
                    setPage(0);
                  }}
                />
              </>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <SchemaDialogForm
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={dialogTitle}
        initialRecord={editingRecord}
        schema={customerFormSchema}
        onSubmit={submit}
        deps={{
          agents,
          agentsLoading,
          onSearchAgents: loadAgents,
        }}
      />

      <ConfirmationDialog
        open={confirmationDialogOpen}
        title="Delete Customer"
        message={confirmationDialogMessage}
        onCancel={() => setConfirmationDialogOpen(false)}
        onOk={onDeleteConfirmed}
      />

      <MDSnackbar
        color={snack.color}
        icon="notifications"
        title="Loyalty System"
        content={snack.content}
        open={snack.open}
        onClose={closeSnack}
        close={closeSnack}
        bgWhite
      />
    </DashboardLayout>
  );
}
